import React, { useState, useEffect, useMemo, useContext } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { IMAGES, bgStyle } from '@/config/images';
import {
  Search,
  Droplet,
  Layers,
  Sparkles,
  Wrench,
  Box,
  FlaskConical,
  ChevronRight,
  ArrowLeft,
  AlertTriangle,
  CheckCircle,
  Lightbulb,
  X,
  Check,
  StickyNote,
  Clock,
  Bot,
  Printer,
  MessageSquare
} from 'lucide-react';
import axios from 'axios';
import { API, AuthContext } from '@/App';

// Category icons mapping
const categoryIcons = {
  extrusion: Droplet,
  adhesion: Layers,
  quality: Sparkles,
  mechanical: Wrench,
  material: Box,
  resin: FlaskConical
};

const categoryColors = {
  extrusion: { bg: 'from-blue-500/50 to-blue-600/30', border: 'border-blue-500/50', text: 'text-blue-400' },
  adhesion: { bg: 'from-purple-500/50 to-purple-600/30', border: 'border-purple-500/50', text: 'text-purple-400' },
  quality: { bg: 'from-yellow-500/50 to-yellow-600/30', border: 'border-yellow-500/50', text: 'text-yellow-400' },
  mechanical: { bg: 'from-red-500/50 to-red-600/30', border: 'border-red-500/50', text: 'text-red-400' },
  material: { bg: 'from-green-500/50 to-green-600/30', border: 'border-green-500/50', text: 'text-green-400' },
  resin: { bg: 'from-cyan-500/50 to-cyan-600/30', border: 'border-cyan-500/50', text: 'text-cyan-400' }
};

const PrintTroubleshooting = () => {
  const { user } = useContext(AuthContext);
  const token = localStorage.getItem('token');

  const [issues, setIssues] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [userAttempts, setUserAttempts] = useState([]);
  const [expandedNotes, setExpandedNotes] = useState({});
  const [noteInputs, setNoteInputs] = useState({});
  const [savingAttempt, setSavingAttempt] = useState(null);
  
  // AI Chat state
  const [userPrinters, setUserPrinters] = useState([]);
  const [selectedPrinter, setSelectedPrinter] = useState('');
  const [selectedSlicer, setSelectedSlicer] = useState('');
  
  // Slicer software options for 3D printing
  const SLICER_OPTIONS = [
    'Cura',
    'PrusaSlicer',
    'OrcaSlicer',
    'Bambu Studio',
    'SuperSlicer',
    'Simplify3D',
    'IdeaMaker',
    'Creality Print',
    'FlashPrint',
    'Chitubox (Resin)',
    'Lychee Slicer (Resin)',
    'Other',
  ];

  useEffect(() => {
    fetchData();
    fetchUserPrinters();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedIssue && token) {
      fetchUserAttempts(selectedIssue.id);
    }
  }, [selectedIssue, token]);

  const fetchData = async () => {
    try {
      const [issuesRes, categoriesRes] = await Promise.all([
        axios.get(`${API}/printing3d/troubleshooting/issues`),
        axios.get(`${API}/printing3d/troubleshooting/categories`)
      ]);
      setIssues(issuesRes.data);
      setCategories(categoriesRes.data);
    } catch (error) {
      console.error('Failed to fetch troubleshooting data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchUserPrinters = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/printing3d/printers`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserPrinters(response.data || []);
      // Auto-select first printer if available
      if (response.data?.length > 0) {
        setSelectedPrinter(response.data[0].id);
      }
    } catch (error) {
      console.error('Failed to fetch user printers:', error);
    }
  };

  const fetchUserAttempts = async (issueId) => {
    if (!token) return;
    try {
      const response = await axios.get(`${API}/printing3d/troubleshooting/attempts?issue_id=${issueId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserAttempts(response.data);
    } catch (error) {
      console.error('Failed to fetch user attempts:', error);
    }
  };

  const handleMarkTried = async (action) => {
    if (!token || !selectedIssue) return;
    setSavingAttempt(action);
    try {
      await axios.post(`${API}/printing3d/troubleshooting/attempts`, {
        issue_id: selectedIssue.id,
        action_tried: action,
        notes: noteInputs[action] || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchUserAttempts(selectedIssue.id);
      setNoteInputs(prev => ({ ...prev, [action]: '' }));
      setExpandedNotes(prev => ({ ...prev, [action]: false }));
    } catch (error) {
      console.error('Failed to save attempt:', error);
    } finally {
      setSavingAttempt(null);
    }
  };

  const handleDeleteAttempt = async (attemptId) => {
    if (!token) return;
    try {
      await axios.delete(`${API}/printing3d/troubleshooting/attempts/${attemptId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      await fetchUserAttempts(selectedIssue.id);
    } catch (error) {
      console.error('Failed to delete attempt:', error);
    }
  };

  const isActionTried = (action) => {
    return userAttempts.some(a => a.action_tried === action);
  };

  const getAttemptForAction = (action) => {
    return userAttempts.find(a => a.action_tried === action);
  };

  const filteredIssues = useMemo(() => {
    let filtered = issues;
    
    if (selectedCategory) {
      filtered = filtered.filter(issue => issue.category === selectedCategory);
    }
    
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(issue => 
        issue.issue_name.toLowerCase().includes(query) ||
        issue.explanation.toLowerCase().includes(query) ||
        issue.causes.some(c => c.toLowerCase().includes(query)) ||
        issue.actions.some(a => a.toLowerCase().includes(query))
      );
    }
    
    return filtered;
  }, [issues, selectedCategory, searchQuery]);

  if (isLoading) {
    return (
      <div 
        className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6 flex items-center justify-center"
        style={bgStyle(IMAGES.PRINTING3D_TROUBLESHOOTING)}
      >
        <div className="fixed inset-0 bg-black/70 -z-10" style={{ pointerEvents: 'none' }} />
        <div className="text-center">
          <Wrench className="w-12 h-12 text-orange-500 mx-auto mb-4 animate-pulse" />
          <p className="text-zinc-400">Loading troubleshooting guide...</p>
        </div>
      </div>
    );
  }

  // Issue Detail View
  if (selectedIssue) {
    const colors = categoryColors[selectedIssue.category] || categoryColors.quality;
    const CategoryIcon = categoryIcons[selectedIssue.category] || Wrench;
    
    return (
      <div 
        className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" 
        data-testid="print-troubleshooting-detail"
        style={bgStyle(IMAGES.PRINTING3D_TROUBLESHOOTING)}
      >
        <div className="fixed inset-0 bg-black/70 -z-10" style={{ pointerEvents: 'none' }} />
        {/* Back Button */}
        <button 
          onClick={() => setSelectedIssue(null)}
          className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6 transition-colors"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Issues
        </button>

        <div className="max-w-4xl mx-auto">
          {/* Issue Header */}
          <div className={`p-6 rounded-xl bg-gradient-to-br ${colors.bg} border ${colors.border} mb-6`}>
            <div className="flex items-start gap-4">
              <div className={`p-3 rounded-lg ${colors.text} bg-black/20`}>
                <CategoryIcon className="w-8 h-8" />
              </div>
              <div className="flex-1">
                <Badge className="bg-zinc-800 text-zinc-300 mb-2 capitalize">{selectedIssue.category}</Badge>
                <h1 className="text-2xl font-bold text-white mb-2">{selectedIssue.issue_name}</h1>
                <p className="text-zinc-300">{selectedIssue.explanation}</p>
              </div>
            </div>
          </div>

          {/* Resin Safety Warning */}
          {selectedIssue.category === 'resin' && (
            <div className="flex items-start gap-3 p-4 rounded-xl bg-red-900/40 border border-red-500/50 mb-6">
              <AlertTriangle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-red-300 font-semibold text-sm mb-1">Resin Safety — Read Before Proceeding</p>
                <p className="text-red-200 text-sm">Uncured resin is toxic and a skin sensitiser. Wear nitrile gloves and eye protection at all times. Work in a ventilated area. Never dispose of liquid resin or IPA/resin mix down the drain — cure under UV until solid, then dispose as solid waste.</p>
              </div>
            </div>
          )}

          {/* Common Causes */}
          <Card className="bg-black/70 backdrop-blur-sm border-zinc-700/50 mb-6">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-500" />
                Common Causes
              </CardTitle>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {selectedIssue.causes.map((cause, idx) => (
                  <li key={idx} className="flex items-start gap-3 text-zinc-300">
                    <span className="w-6 h-6 rounded-full bg-yellow-500/20 text-yellow-500 flex items-center justify-center text-sm flex-shrink-0 mt-0.5">
                      {idx + 1}
                    </span>
                    {cause}
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          {/* Actions to Try */}
          <Card className="bg-black/70 backdrop-blur-sm border-zinc-700/50 mb-6">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Wrench className="w-5 h-5 text-blue-500" />
                What to Try
              </CardTitle>
              <CardDescription className="text-zinc-400">
                Work through these solutions in order. Mark items as tried to track your progress.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {selectedIssue.actions.map((action, idx) => {
                  const tried = isActionTried(action);
                  const attempt = getAttemptForAction(action);
                  const notesExpanded = expandedNotes[action];
                  
                  return (
                    <div 
                      key={idx} 
                      className={`p-4 rounded-lg border transition-all ${
                        tried 
                          ? 'bg-green-900/20 border-green-600/30' 
                          : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-600'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm flex-shrink-0 ${
                          tried 
                            ? 'bg-green-500 text-white' 
                            : 'bg-blue-500/20 text-blue-500'
                        }`}>
                          {tried ? <Check className="w-4 h-4" /> : idx + 1}
                        </span>
                        <div className="flex-1">
                          <p className={`${tried ? 'text-green-300' : 'text-zinc-300'}`}>{action}</p>
                          
                          {/* Show attempt notes if exists */}
                          {attempt?.notes && (
                            <div className="mt-2 p-2 bg-black/20 rounded text-sm text-zinc-400 flex items-start gap-2">
                              <StickyNote className="w-4 h-4 flex-shrink-0 mt-0.5" />
                              <span>{attempt.notes}</span>
                            </div>
                          )}
                          
                          {/* Notes input when expanded */}
                          {notesExpanded && !tried && (
                            <div className="mt-3 flex gap-2">
                              <Input
                                placeholder="Optional: Add notes about what happened..."
                                value={noteInputs[action] || ''}
                                onChange={(e) => setNoteInputs(prev => ({ ...prev, [action]: e.target.value }))}
                                className="bg-zinc-900 border-zinc-700 text-white text-sm"
                              />
                            </div>
                          )}
                        </div>
                        
                        {/* Action buttons */}
                        <div className="flex items-center gap-2">
                          {tried ? (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-zinc-400 hover:text-red-400"
                              onClick={() => attempt && handleDeleteAttempt(attempt.id)}
                            >
                              <X className="w-4 h-4" />
                            </Button>
                          ) : token ? (
                            <>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-zinc-400 hover:text-zinc-200"
                                onClick={() => setExpandedNotes(prev => ({ ...prev, [action]: !notesExpanded }))}
                              >
                                <StickyNote className="w-4 h-4" />
                              </Button>
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-500"
                                onClick={() => handleMarkTried(action)}
                                disabled={savingAttempt === action}
                              >
                                {savingAttempt === action ? (
                                  <Clock className="w-4 h-4 animate-spin" />
                                ) : (
                                  <Check className="w-4 h-4" />
                                )}
                              </Button>
                            </>
                          ) : null}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          {/* Recommendation */}
          <Card className="bg-gradient-to-br from-orange-500/10 to-orange-600/5 border-orange-500/30">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-orange-500" />
                Pro Tip
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-zinc-300">{selectedIssue.recommendation}</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Main Issue List View
  return (
    <div 
      className="min-h-screen -m-4 lg:-m-6 p-4 lg:p-6" 
      data-testid="print-troubleshooting"
      style={bgStyle(IMAGES.PRINTING3D_TROUBLESHOOTING)}
    >
      <div className="fixed inset-0 bg-black/70 -z-10" style={{ pointerEvents: 'none' }} />
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center gap-4">
          <div className="p-3 bg-orange-500/20 rounded-lg">
            <Wrench className="w-8 h-8 text-orange-500" />
          </div>
          <div>
            <div className="text-xs font-bold uppercase tracking-wider text-orange-500 mb-1">DIAGNOSE & FIX</div>
            <h1 
              className="text-3xl md:text-4xl font-bold text-orange-500 tracking-tight uppercase"
              style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
            >
              Print Troubleshooting
            </h1>
            <p className="text-sm md:text-base text-zinc-300 mt-1">Find and fix common printing problems</p>
          </div>
        </div>
      </div>

      {/* Ask AI Section - Two Panel Layout like CNC Router */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* Printer/Hardware Issues Card */}
        <div className="bg-black/70 backdrop-blur-sm border border-zinc-700/50 rounded-lg p-6 hover:border-orange-500/50 transition-colors">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-orange-500/20 border border-orange-500/30 flex items-center justify-center rounded-lg">
              <Printer className="w-5 h-5 text-orange-500" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Printer / Hardware Issues</h3>
              <p className="text-xs text-zinc-400">Stringing, warping, adhesion, extrusion problems</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <div className="text-xs text-zinc-500 uppercase tracking-wide">Select your printer</div>
              <Select value={selectedPrinter} onValueChange={setSelectedPrinter}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="printer-select">
                  <SelectValue placeholder={userPrinters.length ? 'Select printer' : 'No printers configured'} />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {userPrinters.map((printer) => (
                    <SelectItem key={printer.id} value={printer.id} className="text-white hover:bg-zinc-700">
                      {printer.name} {printer.brand && `(${printer.brand})`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {userPrinters.length === 0 && (
                <Link to="/3d-printing/setup" className="text-xs text-orange-400 hover:text-orange-300">
                  + Add a printer first
                </Link>
              )}
            </div>

            <Link 
              to={selectedPrinter ? `/3d-printing/ask-ai?printer_id=${selectedPrinter}` : '/3d-printing/setup'}
              className="block"
            >
              <Button
                className="w-full bg-orange-600 hover:bg-orange-500 text-white font-bold"
                disabled={!selectedPrinter}
                data-testid="troubleshoot-printer-ask-ai"
              >
                <Bot className="w-4 h-4 mr-2" />
                Ask AI about this printer
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>

        {/* Slicer/Software Issues Card */}
        <div className="bg-black/70 backdrop-blur-sm border border-zinc-700/50 rounded-lg p-6 hover:border-blue-500/50 transition-colors">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-blue-500/20 border border-blue-500/30 flex items-center justify-center rounded-lg">
              <Layers className="w-5 h-5 text-blue-500" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-white">Slicer / Software Issues</h3>
              <p className="text-xs text-zinc-400">Settings, profiles, G-code, slicing problems</p>
            </div>
          </div>

          <div className="space-y-4">
            <div className="space-y-2">
              <div className="text-xs text-zinc-500 uppercase tracking-wide">Select slicer software</div>
              <Select value={selectedSlicer} onValueChange={setSelectedSlicer}>
                <SelectTrigger className="bg-zinc-800 border-zinc-700 text-white" data-testid="slicer-select">
                  <SelectValue placeholder="Select slicer" />
                </SelectTrigger>
                <SelectContent className="bg-zinc-800 border-zinc-700">
                  {SLICER_OPTIONS.map((slicer) => (
                    <SelectItem key={slicer} value={slicer} className="text-white hover:bg-zinc-700">
                      {slicer}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <Link 
              to={selectedSlicer ? `/3d-printing/ask-ai?slicer=${encodeURIComponent(selectedSlicer)}` : '#'}
              className="block"
            >
              <Button
                className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold"
                disabled={!selectedSlicer}
                data-testid="troubleshoot-slicer-ask-ai"
              >
                <Bot className="w-4 h-4 mr-2" />
                Ask AI about this slicer
                <ChevronRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </div>

      {/* Quick Tip */}
      <div className="bg-black/70 backdrop-blur-sm border border-yellow-500/30 rounded-lg p-4 mb-6">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-yellow-500 mt-0.5 shrink-0" />
          <div>
            <h3 className="text-sm font-semibold text-white mb-1">Quick tip</h3>
            <p className="text-sm text-zinc-400">
              For best results, keep your printer profile updated — the AI will use your printer's 
              extruder type, hotend, bed surface, and firmware to suggest the right fixes.
            </p>
            <Link 
              to="/3d-printing/setup" 
              className="inline-flex items-center gap-1 mt-2 text-sm text-orange-400 hover:text-orange-300 transition-colors"
            >
              Review Printer Setup
              <ChevronRight className="w-3 h-3" />
            </Link>
          </div>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-zinc-400" />
        <Input
          placeholder="Search issues... (e.g., stringing, warping, adhesion)"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10 bg-black/70 backdrop-blur-sm border-zinc-700/50 text-white placeholder:text-zinc-500"
        />
      </div>

      {/* Categories */}
      <div className="flex flex-wrap gap-2 mb-6">
        <Button
          variant={selectedCategory === null ? "default" : "outline"}
          onClick={() => setSelectedCategory(null)}
          className={selectedCategory === null ? "bg-orange-600" : "border-zinc-700"}
        >
          All Issues
        </Button>
        {categories.map((cat) => {
          const CatIcon = categoryIcons[cat.id] || Wrench;
          const isActive = selectedCategory === cat.id;
          return (
            <Button
              key={cat.id}
              variant={isActive ? "default" : "outline"}
              onClick={() => setSelectedCategory(cat.id)}
              className={isActive ? "bg-orange-600" : "border-zinc-700"}
            >
              <CatIcon className="w-4 h-4 mr-2" />
              {cat.name}
            </Button>
          );
        })}
      </div>

      {/* Issues Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {filteredIssues.map((issue) => {
          const colors = categoryColors[issue.category] || categoryColors.quality;
          const CategoryIcon = categoryIcons[issue.category] || Wrench;

          return (
            <Card
              key={issue.id}
              className="bg-black/70 backdrop-blur-sm border border-zinc-700/50 hover:border-orange-500/50 transition-all cursor-pointer"
              onClick={() => setSelectedIssue(issue)}
            >
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CategoryIcon className={`w-6 h-6 ${colors.text}`} />
                  <Badge className="bg-zinc-800 text-zinc-300 capitalize">{issue.category}</Badge>
                </div>
                <CardTitle className="text-lg text-white mt-2">{issue.issue_name}</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-zinc-300 line-clamp-2 mb-3">{issue.explanation}</p>
                <div className="flex items-center gap-1 text-[#e67e22] text-sm">
                  <span>View solutions</span>
                  <ChevronRight className="w-4 h-4" />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {filteredIssues.length === 0 && (
        <div className="text-center py-12">
          <Search className="w-12 h-12 text-zinc-600 mx-auto mb-4" />
          <h3 className="text-xl text-zinc-400 mb-2">No issues found</h3>
          <p className="text-zinc-500">Try adjusting your search or category filter</p>
        </div>
      )}
    </div>
  );
};

export default PrintTroubleshooting;
